// Consent-gated tracker loading.
//
// PostHog (product analytics + session recording) and the X ads pixel both used to fire from
// index.html on first paint, before anyone had a chance to say no. Session recording in
// particular is not something you can defend as "strictly necessary", so inside the EU/EEA/UK
// they now wait for an explicit Accept.
//
// Outside those regions the trackers load immediately, exactly as they did before — the banner
// never appears and no analytics data is lost.
//
// Vercel Analytics and Speed Insights are deliberately NOT gated here: they are cookieless and
// aggregate, they set no identifiers, and they stay mounted in App.js unconditionally.

const STORAGE_KEY = "ft_cookie_consent_v1";

const POSTHOG_KEY = "phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs";
const POSTHOG_HOST = "https://us.i.posthog.com";
const POSTHOG_ASSETS = "https://us-assets.i.posthog.com";
const X_PIXEL_ID = "re1ie";

// EEA members whose IANA zone does not start with "Europe/", plus the outermost regions that are
// still covered by the GDPR. Everything under "Europe/" is caught by the prefix test below —
// that also sweeps in a few non-EU countries (Moscow, Istanbul, Belgrade), which is harmless:
// the worst case is showing a banner to someone who did not legally need one.
const EXTRA_CONSENT_ZONES = new Set([
  "Atlantic/Reykjavik",   // Iceland
  "Atlantic/Faroe",       // Faroe Islands
  "Atlantic/Canary",      // Spain
  "Atlantic/Madeira",     // Portugal
  "Atlantic/Azores",      // Portugal
  "Asia/Nicosia",         // Cyprus
  "Asia/Famagusta",       // Cyprus
  "America/Cayenne",      // French Guiana
  "America/Guadeloupe",
  "America/Martinique",
  "Indian/Reunion",
  "Indian/Mayotte",
]);

/**
 * Whether this visitor is somewhere that needs an opt-in before we load trackers.
 *
 * Time zone is the only signal available to a static build — there is no server render to read
 * Vercel's geo headers from, and an IP lookup would add a network round trip in front of every
 * page view. If the browser will not tell us, we assume consent IS required rather than
 * quietly tracking someone we could not place.
 */
export function isConsentRegion() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!tz) return true;
    return tz.startsWith("Europe/") || EXTRA_CONSENT_ZONES.has(tz);
  } catch {
    return true;
  }
}

/** "granted" | "denied" | null (no decision recorded yet). */
export function getConsent() {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
}

export function setConsent(value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Private mode / storage blocked. The decision holds for this page view only, which is the
    // safe direction to fail in — next visit we ask again instead of assuming a yes.
  }
}

/** True when we are allowed to load PostHog and the X pixel right now. */
export function trackersAllowed() {
  if (!isConsentRegion()) return true;
  return getConsent() === "granted";
}

/** True when the banner should be on screen. */
export function shouldShowBanner() {
  return isConsentRegion() && getConsent() === null;
}

let loaded = false;

/** Inject PostHog and the X pixel. Safe to call more than once. */
export function loadTrackers() {
  if (loaded || typeof document === "undefined") return;
  loaded = true;
  loadPostHog();
  loadXPixel();
}

function loadPostHog() {
  if (window.posthog) return;
  const s = document.createElement("script");
  s.src = `${POSTHOG_ASSETS}/static/array.js`;
  s.async = true;
  s.crossOrigin = "anonymous";
  s.onload = () => {
    if (!window.posthog || !window.posthog.init) return;
    window.posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: "identified_only",
      session_recording: { recordCrossOriginIframes: true, capturePerformance: false },
    });
  };
  document.head.appendChild(s);
}

function loadXPixel() {
  // Same stub the official uwt.js snippet installs: queue calls now, uwt.js replays them once it
  // sets twq.exe. Success.jsx fires its purchase event through window.twq and already no-ops when
  // the pixel is absent, so a declined visitor simply reports no conversion.
  if (!window.twq) {
    const twq = function () {
      twq.exe ? twq.exe.apply(twq, arguments) : twq.queue.push(arguments);
    };
    twq.version = "1.1";
    twq.queue = [];
    window.twq = twq;
  }
  const s = document.createElement("script");
  s.src = "https://static.ads-twitter.com/uwt.js";
  s.async = true;
  document.head.appendChild(s);
  window.twq("config", X_PIXEL_ID);
}
